import { useState, type JSX } from 'react';
import { Box, Slide, Typography, useMediaQuery } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import useLocalization from '../../../../hooks/useLocalization';
import { mergeSx } from '../../../../utils/styles';
import { formatDisplayDate, formatScore, getResultBadgeStyles, getResultForTrackedTeam } from '../helpers';
import { getMobileAnchoredPanelSx, MOBILE_TABBED_WIDGET_MEDIA_QUERY, styles } from '../styles';
import type { PreMatchWidgetMatch, PreMatchWidgetTeam, TeamSide } from '../types';
import TeamVisual from './TeamVisual';

type TeamMatchesPanelProps = {
  competitionName?: string | null;
  team: PreMatchWidgetTeam;
  matches: PreMatchWidgetMatch[];
  side?: TeamSide;
  sideAligned?: boolean;
  hideTeamHeader?: boolean;
  panelSx?: SxProps<Theme>;
};

const MOBILE_MATCH_TEAMS = ['home', 'away'] as const;

export const TeamFormPanel = ({
  competitionName,
  team,
  matches,
  side = 'home',
  sideAligned = false,
  hideTeamHeader = false,
  panelSx,
}: TeamMatchesPanelProps) => {
  const { t } = useLocalization();
  const boxSx = mergeSx(styles.teamPanel, panelSx, sideAligned ? getMobileAnchoredPanelSx() : undefined);

  return (
    <Box sx={boxSx}>
      {!hideTeamHeader ? (
        <Box sx={mergeSx(styles.teamPanelHeader, sideAligned && side === 'away' && styles.mobileAnchoredHeaderRight)}>
          <TeamVisual competitionName={competitionName} team={team} sx={styles.panelTeamVisual} />
          <Typography
            sx={mergeSx(styles.teamPanelTitle, sideAligned && side === 'away' && styles.mobileAnchoredTextRight)}
          >
            {team.name}
          </Typography>
        </Box>
      ) : null}
      {matches.length ? (
        <Box sx={styles.playerList}>
          {matches.map((match) => {
            const resultForTrackedTeam = getResultForTrackedTeam(match, team);
            const homeTeamLabel = match.homeTeamShortName ?? match.homeTeam;
            const awayTeamLabel = match.awayTeamShortName ?? match.awayTeam;

            return (
              <Box key={`${team.name}-${match.date}-${match.homeTeam}-${match.awayTeam}`} sx={styles.upcomingItem}>
                <Typography sx={styles.formMatchMeta}>
                  {formatDisplayDate(match.date, false)} {match.competition}
                </Typography>
                <Box sx={styles.directMatchRow}>
                  <Typography sx={styles.directMatchTeam}>{homeTeamLabel}</Typography>
                  <Box
                    sx={mergeSx(
                      styles.formMatchScorePill,
                      resultForTrackedTeam ? getResultBadgeStyles(resultForTrackedTeam) : undefined
                    )}
                  >
                    <Typography sx={{ typography: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
                      {formatScore(match.score) ?? '-'}
                    </Typography>
                  </Box>
                  <Typography sx={mergeSx(styles.directMatchTeam, styles.directMatchTeamRight)}>
                    {awayTeamLabel}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography
          sx={mergeSx(styles.summaryDescription, sideAligned && side === 'away' && styles.mobileAnchoredTextRight)}
        >
          {t('preMatchWidget.matches.noRecent')}
        </Typography>
      )}
    </Box>
  );
};

export const UpcomingMatchesPanel = ({
  team,
  matches,
  side = 'home',
  sideAligned = false,
  panelSx,
}: TeamMatchesPanelProps) => {
  const { t } = useLocalization();
  const boxSx = mergeSx(styles.teamPanel, panelSx, sideAligned ? getMobileAnchoredPanelSx() : undefined);

  return (
    <Box sx={boxSx}>
      {matches.length ? (
        <Box sx={styles.playerList}>
          {matches.map((match) => {
            const homeTeamLabel = match.homeTeamShortName ?? match.homeTeam;
            const awayTeamLabel = match.awayTeamShortName ?? match.awayTeam;

            return (
              <Box key={`${team.name}-${match.date}-${match.homeTeam}-${match.awayTeam}`} sx={styles.upcomingItem}>
                <Typography sx={styles.formMatchMeta}>
                  {formatDisplayDate(match.date, false)} {match.competition}
                </Typography>
                <Box sx={styles.directMatchRow}>
                  <Typography sx={styles.directMatchTeam}>{homeTeamLabel}</Typography>
                  <Typography sx={styles.upcomingFixtureDivider}>{t('preMatchWidget.matches.versus')}</Typography>
                  <Typography sx={mergeSx(styles.directMatchTeam, styles.directMatchTeamRight)}>
                    {awayTeamLabel}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography
          sx={mergeSx(styles.summaryDescription, sideAligned && side === 'away' && styles.mobileAnchoredTextRight)}
        >
          {t('preMatchWidget.matches.noUpcoming')}
        </Typography>
      )}
    </Box>
  );
};

type ResponsiveTeamMatchesPanelsProps = {
  competitionName?: string | null;
  homeTeam: PreMatchWidgetTeam;
  awayTeam: PreMatchWidgetTeam;
  homeMatches: PreMatchWidgetMatch[];
  awayMatches: PreMatchWidgetMatch[];
  tablistLabel: string;
  mobileHeroLabel?: string;
  panelSx?: SxProps<Theme>;
  mobilePanelSx?: SxProps<Theme>;
  renderPanel: (props: TeamMatchesPanelProps) => JSX.Element;
};

export const ResponsiveTeamMatchesPanels = ({
  competitionName,
  homeTeam,
  awayTeam,
  homeMatches,
  awayMatches,
  tablistLabel,
  mobileHeroLabel,
  panelSx,
  mobilePanelSx,
  renderPanel,
}: ResponsiveTeamMatchesPanelsProps) => {
  const [activeTeam, setActiveTeam] = useState<TeamSide>('home');
  const useTabbedMobileLayout = useMediaQuery(MOBILE_TABBED_WIDGET_MEDIA_QUERY);
  const homePanel = renderPanel({ competitionName, team: homeTeam, matches: homeMatches, side: 'home', panelSx });
  const awayPanel = renderPanel({ competitionName, team: awayTeam, matches: awayMatches, side: 'away', panelSx });
  const fixtureHero = mobileHeroLabel ? (
    <Box sx={styles.matchesMobileHero}>
      <Box sx={styles.matchesMobileHeroTeam}>
        <TeamVisual competitionName={competitionName} team={homeTeam} sx={styles.h2hHeroTeamVisual} />
        <Typography sx={styles.matchesMobileHeroTeamLabel}>{homeTeam.shortName ?? homeTeam.name}</Typography>
      </Box>
      <Box sx={styles.matchesMobileHeroCenter}>
        <Typography sx={styles.h2hHeroVs}>VS</Typography>
        <Typography sx={styles.matchesMobileHeroMeta}>{mobileHeroLabel}</Typography>
      </Box>
      <Box sx={mergeSx(styles.matchesMobileHeroTeam, styles.matchesMobileHeroTeamRight)}>
        <TeamVisual competitionName={competitionName} team={awayTeam} sx={styles.h2hHeroTeamVisual} />
        <Typography sx={styles.matchesMobileHeroTeamLabel}>{awayTeam.shortName ?? awayTeam.name}</Typography>
      </Box>
    </Box>
  ) : null;

  if (!useTabbedMobileLayout) {
    return (
      <Box sx={styles.matchPanelsShell}>
        {fixtureHero}
        <Box sx={styles.teamPanels}>
          {homePanel}
          {awayPanel}
        </Box>
      </Box>
    );
  }

  const selectedTeam = activeTeam === 'home' ? homeTeam : awayTeam;

  return (
    <Box sx={styles.matchPanelsShell}>
      <Box sx={styles.mobileLineupSelector} role='tablist' aria-label={tablistLabel}>
        {MOBILE_MATCH_TEAMS.map((side) => [side, side === 'home' ? homeTeam : awayTeam] as const).map(
          ([side, team]) => {
            const isActive = activeTeam === side;

            return (
              <Box
                key={side}
                component='button'
                type='button'
                role='tab'
                aria-selected={isActive}
                onClick={() => setActiveTeam(side)}
                sx={mergeSx(
                  styles.mobileLineupSelectorButton,
                  side === 'home' ? styles.mobileLineupSelectorButtonLeft : styles.mobileLineupSelectorButtonRight
                )}
              >
                <TeamVisual competitionName={competitionName} team={team} sx={styles.mobileTabTeamVisual} />
                <Typography
                  sx={mergeSx(
                    styles.mobileLineupSelectorLabel,
                    isActive && styles.mobileLineupSelectorLabelActive,
                    side === 'away' && styles.mobileLineupSelectorLabelRight
                  )}
                >
                  {team.shortName ?? team.name}
                </Typography>
              </Box>
            );
          }
        )}
      </Box>
      <Box sx={styles.mobileLineupSelectorIndicator} aria-hidden='true'>
        {MOBILE_MATCH_TEAMS.map((side) => (
          <Box
            key={side}
            sx={mergeSx(
              styles.mobileLineupSelectorIndicatorSegment,
              side === activeTeam && styles.mobileLineupSelectorIndicatorSegmentActive
            )}
          />
        ))}
      </Box>
      <Box
        key={selectedTeam.name}
        sx={styles.mobileLineupStage}
        data-testid='mobile-team-panel'
        data-team-side={activeTeam}
      >
        <Slide
          key={activeTeam}
          in
          direction={activeTeam === 'home' ? 'right' : 'left'}
          mountOnEnter
          unmountOnExit
          appear={false}
          timeout={250}
        >
          <Box sx={styles.mobileLineupSlide}>
            {renderPanel({
              competitionName,
              team: selectedTeam,
              matches: activeTeam === 'home' ? homeMatches : awayMatches,
              side: activeTeam,
              sideAligned: true,
              hideTeamHeader: true,
              panelSx: mobilePanelSx ?? panelSx,
            })}
          </Box>
        </Slide>
      </Box>
    </Box>
  );
};
